import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect, useDispatch } from 'react-redux'
import { Link } from 'react-router'
import { selectors as globalSchoolSelectors } from 'school-portal-reducer'
import { selectors as globalSelectors } from 'global-reducer'
import { selectors as summarySelectors } from '../reducer'
import * as Types from 'types'
import { ArtsLiaisonForm, AccessibleProgramsForm } from '../forms'
import { PortalBadgeForm } from 'forms'
import * as apiActions from 'api-actions'
import { DashboardMap } from '../components'
import { Tooltip } from 'components'
import { ArrowRightIcon } from 'components/icons'
import { Spinner, Button, ButtonArea } from 'lp-components'
import SurveyCompletionModal from '../components/survey-completion-modal'
import {
  serializeLocation,
  replaceResources,
  displaySubmitFailure,
  useCommunity,
  getOtherTypeId,
  combineOtherOption,
  filterDestroyed,
  yankBy,
  isOtherType,
  useMultiFormSave,
} from 'utils'
import * as flashActions from 'redux-flash'
import {
  startCase,
  get,
  reduce,
  flatMap,
  values,
  filter,
  isEmpty,
  camelCase,
} from 'lodash'
import { set } from 'lodash/fp'
import { WHATS_HAPPENING_NOW_TEXT, SCHOOL_PORTAL_ROUTE } from 'config'
import { SubmissionError } from 'redux-form'
import { ArtsSpaceForm } from '../../survey/forms'
import { selectors } from '../../survey/reducer'
import { toggle } from '@launchpadlab/lp-hoc'
import * as actions from '../actions'
import syncProfileCompletion from '../sync-profile-completion'
import { buildInterestCompletionOptions } from '../profile-completion-sections'

const propTypes = {
  disciplines: PropTypes.arrayOf(Types.discipline).isRequired,
  fetchPositions: PropTypes.func.isRequired,
  flashSuccessMessage: PropTypes.func.isRequired,
  liaisonPosition: Types.position,
  location: Types.location,
  participation: Types.participation,
  school: Types.school,
  schoolYears: PropTypes.arrayOf(Types.schoolYear),
  updateSchool: PropTypes.func.isRequired,
  fetchSpaceTypes: PropTypes.func.isRequired,
  spaceTypes: PropTypes.arrayOf(Types.spaceType),
  fetchSchool: PropTypes.func.isRequired,
  fetchCompletionSchool: PropTypes.func.isRequired,
  currentSchoolId: PropTypes.number.isRequired,
  currentSchoolYear: PropTypes.object.isRequired,
  routerLocation: PropTypes.object.isRequired,
  interestCompletionOptions: PropTypes.object,
}

const defaultProps = {
  liaisonPosition: null,
  location: null,
  participation: null,
  spaceTypes: null,
  interestCompletionOptions: {},
}

function getDisciplineForProgram(program, disciplines) {
  const subDisciplineId = program.subDisciplineId || program.id
  return disciplines.find(({ subDisciplines }) =>
    subDisciplines.some(({ id }) => id === subDisciplineId)
  )
}

function groupProgramsByDiscipline(programs, disciplines) {
  return reduce(
    programs,
    (disciplinesCollection, program) => {
      const disciplineName = getDisciplineForProgram(program, disciplines).name
      return set(
        `${disciplineName}.${program.name}`,
        program,
        disciplinesCollection
      )
    },
    {}
  )
}

function getListOfAccessiblePrograms(programsGroupedByDiscipline) {
  const programs = flatMap(programsGroupedByDiscipline, values)
  return filter(
    programs,
    ({ tag }) => tag !== Types.SUB_DISCIPLINE_OFFER_TYPES.NONE
  )
}

function getParticipationYearById(schoolYears, schoolYearId) {
  const schoolYear = schoolYears.find((year) => year.id === schoolYearId)
  return schoolYear ? schoolYear.number : null
}

function Overview({
  disciplines,
  fetchPositions,
  flashSuccessMessage,
  liaisonPosition,
  location,
  participation,
  school,
  schoolYears,
  updateSchool,
  spaceTypes,
  fetchSpaceTypes,
  fetchSchool,
  fetchCompletionSchool,
  currentSchoolId,
  currentSchoolYear,
  routerLocation,
  interestCompletionOptions,
}) {
  useEffect(() => {
    if (!liaisonPosition) fetchPositions()
    if (!spaceTypes) fetchSpaceTypes()
  }, [liaisonPosition, spaceTypes])

  const spaceTypesLoaded = !!spaceTypes
  const schoolLoaded = !!school

  const community = useCommunity()
  const artsLiaisonTooltip = get(
    community,
    'displayText.schoolPortal.summary.artsLiaison.tooltip'
  )

  const currentSchoolYearId = currentSchoolYear.id
  const [otherSpace, setOtherSpace] = useState(null)
  const [definedSpaces, setDefinedSpaces] = useState(null)
  const [showSurveyCompletionModal, setShowSurveyCompletionModal] =
    useState(false)

  useEffect(() => {
    if (schoolLoaded) {
      const [currentOtherSpace, currentDefinedSpaces] = yankBy(
        school?.spaces,
        isOtherType
      )

      setOtherSpace(currentOtherSpace)
      setDefinedSpaces(currentDefinedSpaces)
    }
  }, [school?.spaces])

  const formatForSubmit = useCallback(
    (newCombinedSpaces) => {
      return replaceResources({ old: school?.spaces, new: newCombinedSpaces })
    },
    [school?.spaces]
  )
  const updateSchoolOnSubmit = useCallback(
    (params) => updateSchool(school?.id, currentSchoolYearId, params),
    [school, currentSchoolYearId]
  )
  const onSuccess = useCallback(
    (updatedSchool) => {
      syncProfileCompletion({
        school: updatedSchool,
        previousSchool: school,
        updateSchoolBadge: updateSchoolOnSubmit,
        options: interestCompletionOptions,
      })
      // Keep the current-year completion school in sync so the completion bar
      // reflects this save immediately.
      fetchCompletionSchool(currentSchoolId, currentSchoolYearId)
      flashSuccessMessage(Types.SCHOOL_SUCCESS_MESSAGE)
    },
    [
      flashSuccessMessage,
      school,
      updateSchoolOnSubmit,
      interestCompletionOptions,
      fetchCompletionSchool,
      currentSchoolId,
      currentSchoolYearId,
    ]
  )

  const schoolBadges = useMemo(
    () =>
      (school?.badges || []).filter(
        (b) => b.entityScope === 'school' && b.visibleInUserPortal
      ),
    [school]
  )

  const badgeInitialValues = useMemo(
    () =>
      schoolBadges.reduce((acc, badge) => {
        acc[camelCase(badge.name)] = badge.active
        return acc
      }, {}),
    [schoolBadges]
  )

  // Single page-level Save orchestrates every editable section form. Sections
  // are submitted sequentially (they all PATCH the same school record) and only
  // if every changed section is valid, so we never persist a partial set.
  const { save, saving, formCallbacks } = useMultiFormSave()
  const dispatch = useDispatch()
  const handleSave = useCallback(() => {
    const formNames = [
      'school-arts-space',
      'school-arts-liason',
      'school-accessible-programs',
      ...(schoolBadges.length ? ['portal-badges'] : []),
    ]
    save({
      formNames,
      onComplete: (updatedSchool) => onSuccess(updatedSchool || school),
      onError: ({ errors, submitError } = {}) =>
        displaySubmitFailure(errors, dispatch, submitError),
    })
  }, [save, schoolBadges, onSuccess, school, dispatch])

  useEffect(() => {
    fetchSchool(currentSchoolId, currentSchoolYear.id)
  }, [currentSchoolId, currentSchoolYear.id])

  // Check for survey completion parameter and show modal
  useEffect(() => {
    const urlParams = new URLSearchParams(routerLocation.search)
    if (urlParams.get('surveyCompleted') === 'true') {
      setShowSurveyCompletionModal(true)
      // Clean up the URL parameter
      const newUrl = routerLocation.pathname
      window.history.replaceState({}, '', newUrl)
    }
  }, [routerLocation.search, routerLocation.pathname])

  if (!liaisonPosition || !location || !spaceTypesLoaded || !schoolLoaded)
    return <Spinner />

  const { displayName, category } = school
  const { street, city, state, zip } = location
  const participationSchoolYear = getParticipationYearById(
    schoolYears,
    participation?.schoolYearId
  )

  return (
    <div>
      <br />
      <p>{WHATS_HAPPENING_NOW_TEXT}</p>
      <section className="card card-flex">
        <div className="address-block" data-cy="school-details-container">
          <h2>Your School</h2>
          <ul className="location-details-block">
            <li>
              <p>
                <strong>Name: </strong>
                {displayName}
              </p>
            </li>
            <li>
              <p>
                <strong>School Type: </strong>{' '}
                {category ? startCase(category) : 'Unknown.'}
              </p>
            </li>
            <li>
              <p>
                <strong>Address:</strong> {street} {city} {state} {zip}
              </p>
            </li>
            <li>
              <p>
                <strong>Enrollment Number: </strong>
                {get(participation, 'enrollment', 'None found.')}
                {participationSchoolYear && ` (${participationSchoolYear})`}
              </p>
            </li>
          </ul>
        </div>
        <DashboardMap
          location={serializeLocation(Types.SCHOOL_TYPE, school)}
          hidePopup
        />
      </section>
      <section className="card profile-overview-card">
        <div className="profile-overview-section">
          <h2>
            {get(
              community,
              'displayText.schoolPortal.summary.artsLiaison.header',
              'Arts Liaison'
            )}
            {artsLiaisonTooltip && <Tooltip>{artsLiaisonTooltip}</Tooltip>}
          </h2>
          <ArtsLiaisonForm
            hideSubmit
            onSubmit={(params) =>
              updateSchoolOnSubmit({
                contacts: [
                  {
                    ...params.contacts,
                    positionId: liaisonPosition.id,
                    schoolYearId: currentSchoolYearId,
                  },
                ],
              })
            }
            initialValues={{ contacts: school.artsLiaison }}
            {...formCallbacks}
          />
        </div>
        <hr className="profile-overview-divider" />
        <div className="profile-overview-section">
          <h2>
            Please select the types of arts programs that students at your
            school have access to and whether those programs are offered in
            school, out of school, or both.
          </h2>
          <AccessibleProgramsForm
            hideSubmit
            disciplines={disciplines}
            initialValues={{
              programsByDiscipline: groupProgramsByDiscipline(
                school.accessiblePrograms,
                disciplines
              ),
            }}
            onSubmit={({ programsByDiscipline }) => {
              const resources = replaceResources({
                old: school.accessiblePrograms,
                new: getListOfAccessiblePrograms(programsByDiscipline),
              })
              return updateSchoolOnSubmit({ subDisciplines: resources })
            }}
            {...formCallbacks}
          />
        </div>
        <hr className="profile-overview-divider" />
        <div className="profile-overview-section">
          <h2>
            What physical space does your school have dedicated to the arts?
            Check all that apply.
          </h2>
          <ArtsSpaceForm
            hideSubmit
            spaceTypes={spaceTypes}
            initialValues={{
              other: {
                ...otherSpace,
                spaceTypeId: getOtherTypeId(spaceTypes),
              },
              spaces: definedSpaces,
            }}
            beforeSubmit={({ other, spaces }) => {
              const resources = formatForSubmit(
                combineOtherOption(spaces, other)
              ).map((resource) => ({
                ...resource,
                schoolYearId: currentSchoolYearId,
              }))

              if (isEmpty(filterDestroyed(resources)))
                throw new SubmissionError({
                  _error: 'You must select at least one option',
                })
              return {
                spaces: resources,
              }
            }}
            onSubmit={updateSchoolOnSubmit}
            {...formCallbacks}
          />
        </div>
        {schoolBadges.length > 0 && (
          <React.Fragment>
            <hr className="profile-overview-divider" />
            <div className="profile-overview-section">
              <PortalBadgeForm
                bare
                hideSubmit
                badges={schoolBadges}
                initialValues={badgeInitialValues}
                onSubmit={updateSchoolOnSubmit}
                title="Badges"
                {...formCallbacks}
              />
            </div>
          </React.Fragment>
        )}
        <ButtonArea>
          <Button
            style="primary"
            submitting={saving}
            onClick={handleSave}
            data-cy="save-overview"
          >
            Save
          </Button>
        </ButtonArea>
      </section>
      <div className="profile-section-next">
        <Link
          to={SCHOOL_PORTAL_ROUTE + '/summary/partnerships-with-interests'}
          className="button-secondary profile-completion-button"
        >
          Update Partnerships &amp; Interests
          <ArrowRightIcon />
        </Link>
      </div>
      {showSurveyCompletionModal && (
        <SurveyCompletionModal
          onClose={() => setShowSurveyCompletionModal(false)}
        />
      )}
    </div>
  )
}

Overview.propTypes = propTypes
Overview.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    disciplines: globalSelectors.definedDisciplines(state),
    liaisonPosition: globalSchoolSelectors.liaisonPosition(state),
    location: globalSchoolSelectors.currentSchoolLocation(state),
    participation: globalSchoolSelectors.latestParticipation(state),
    school: summarySelectors.school(state),
    schoolYears: globalSchoolSelectors.activeSchoolYears(state),
    spaceTypes: selectors.spaceTypes(state),
    globalSchool: globalSchoolSelectors.school(state),
    currentSchoolId: globalSchoolSelectors.currentSchoolId(state),
    currentSchoolYear: globalSelectors.currentSchoolYear(state),
    routerLocation: state.routing.locationBeforeTransitions,
    interestCompletionOptions: buildInterestCompletionOptions(state),
  }
}

const mapDispatchToProps = {
  fetchPositions: apiActions.fetchPositions,
  fetchSpaceTypes: apiActions.fetchSpaceTypes,
  fetchSchool: apiActions.fetchSchoolOnCurrentSchoolYear,
  fetchCompletionSchool: apiActions.fetchCompletionSchool,
  flashSuccessMessage: flashActions.flashSuccessMessage,
  updateSchool: apiActions.updateSchool,
  setSchool: actions.setSummarySchool,
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  toggle('newArtsInstructorForm')
)(Overview)
